import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_MONTHLY_BLOOMIE_MARGINS } from '@bloomays-lib/adapter.api-talent';
import type { MonthlyBloomieMargin } from '@bloomays-lib/types.shared';
import type { GetMonthlyBloomieMarginsQueryData } from '../types/primes';

const EMPTY_MARGINS: MonthlyBloomieMargin[] = [];

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export type UseMonthlyBloomieMarginsOptions = {
  emails: string[];
  referenceDate: Date | null;
  onError?: (error: Error) => void;
};

/**
 * PRO-1364 — Marges mensuelles (freelance + CDI + total) des Sales / TAD sélectionnés
 * pour le mois choisi. La requête est ignorée tant qu'aucun Bloomie ou aucun mois n'est sélectionné.
 */
export const useMonthlyBloomieMargins = ({ emails, referenceDate, onError }: UseMonthlyBloomieMarginsOptions) => {
  const skip = emails.length === 0 || referenceDate === null;

  const { loading, error, data } = useQuery<GetMonthlyBloomieMarginsQueryData>(GET_MONTHLY_BLOOMIE_MARGINS, {
    context: { clientName: 'api.talents' },
    variables: {
      emails,
      referenceDate: referenceDate ? referenceDate.toISOString() : null,
    },
    skip,
    fetchPolicy: 'cache-and-network',
    onError,
  });

  const margins = data?.getMonthlyBloomieMargins ?? EMPTY_MARGINS;

  const marginByEmail = useMemo(
    () => new Map(margins.map((margin) => [normalizeEmail(margin.email), margin])),
    [margins],
  );

  return {
    margins,
    marginByEmail,
    loading: !skip && loading,
    error,
  };
};
